// 🔧 use_content.js
// ✅ Purpose: Primary React hook for accessing Content Engine content
// ✅ Features:
// - Provides the only supported way for React pages to retrieve content
// - Wraps the Content Resolver
// - Manages loading and error state
// - Prevents pages from knowing about Firestore, caching, or bundle resolution
// - Future-ready for cache refresh, manifest validation, locale switching,
//   organization overrides, and live content updates

import { useCallback, useEffect, useState } from "react";

import { resolveContent } from "../helpers/content_resolver";

import {
  DEFAULT_SCOPE_ID,
} from "../constants/content_scopes";

import {
  DEFAULT_CONTENT_LOCALE,
} from "../constants/content_locales";

import {
  DEFAULT_CONTENT_PROFILE,
} from "../constants/content_profiles";

/**
 * Canonical Content Engine hook.
 *
 * Example:
 *
 * const {
 *     content,
 *     loading,
 *     error,
 *     refresh,
 * } = useContent({
 *     area: "event_home",
 * });
 */
export default function useContent({
  area,

  scopeId = DEFAULT_SCOPE_ID,

  locale = DEFAULT_CONTENT_LOCALE,

  profile = DEFAULT_CONTENT_PROFILE,
}) {
  const [content, setContent] = useState({});

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  /**
   * Loads one content bundle.
   */
  const loadContent = useCallback(async () => {
    if (!area) {
      console.error(
        "[Content Engine] useContent requires an area."
      );

      setLoading(false);

      return;
    }

    try {
      setLoading(true);

      setError(null);

      const resolvedContent =
        await resolveContent({
          area,
          scopeId,
          locale,
          profile,
        });

      setContent(resolvedContent || {});
    } catch (err) {
      console.error(
        "[Content Engine] Failed to resolve content.",
        err
      );

      setError(err);

      setContent({});
    } finally {
      setLoading(false);
    }
  }, [
    area,
    scopeId,
    locale,
    profile,
  ]);

  /**
   * Initial bundle load.
   */
  useEffect(() => {
    loadContent();
  }, [loadContent]);

  /**
   * Manual refresh.
   *
   * Future:
   *
   * - Manifest refresh
   * - Bundle version refresh
   * - Admin publish refresh
   */
  async function refresh() {
    await loadContent();
  }

  return {
    content,

    loading,

    error,

    refresh,
  };
}